import React, { useCallback, useMemo } from "react";
import {
  ArrowUpRightIcon,
  PlayCircleIcon,
  RocketLaunchIcon,
} from "@heroicons/react/24/solid";
import FlatCard from "components/UI-lib/FlatCard";
import { classNames, roundPriceToTwoDigits } from "components/utils";
import { GPUInstanceType } from "components/Environment/Settings/Tabs/Compute/InstanceChanger/GPUTypes";
import { Tooltip } from "@mui/material";
import { Button } from "@kui/react";
import Footer from "components/Footer";
import LaunchableConfigurationViewFromParams from "../shared/LaunchableConfigurationViewFromParams";
import LaunchableConfigurationView from "../shared/LaunchableConfigurationView";
import { LaunchableNotFound } from "../shared/LaunchableNotFound";
import FileViewer from "../shared/FileViewer";
import { Deploying } from "./Deploying";
import { ProcessedLaunchableData } from "../../utils";
import { LaunchableAccessError } from "./LaunchableAccessError";

interface DeployPageContentProps {
  loading: boolean;
  launchableNotFound?: boolean;
  launchableAccessError?: boolean;
  launchableData?: ProcessedLaunchableData | null;
  intendedInstanceType: string | null;
  intendedDiskStorage: string | null;
  onSetNewCurrentInstanceType?: (
    instanceTypeObject: GPUInstanceType | null,
    diskStorage: string
  ) => void;
  instanceTypeObject: GPUInstanceType | null;
  instanceLoading: boolean;
  instanceStorage: string | null;
  nameErrorMessage: string;
  isAllowedBaseImage: boolean;
  deploymentStarted: boolean;
  isInstancesLoading: boolean;
  allInstancesAvailable: any[];
  isDeploymentFinished: boolean;
  onDeploy: () => void;
  activeOrgId?: string;
  workspaceId?: string;
  lifecycleScript?: string | null;
}

export const DeployPageContent: React.FC<DeployPageContentProps> = ({
  loading,
  launchableNotFound,
  launchableAccessError,
  launchableData,
  intendedInstanceType,
  intendedDiskStorage,
  onSetNewCurrentInstanceType,
  instanceLoading,
  instanceTypeObject,
  instanceStorage,
  nameErrorMessage,
  isAllowedBaseImage,
  deploymentStarted,
  isInstancesLoading,
  allInstancesAvailable,
  isDeploymentFinished,
  onDeploy,
  activeOrgId,
  workspaceId,
  lifecycleScript,
}) => {
  if (!activeOrgId) {
    return <div />;
  }

  // Render helper functions for different states
  const renderLaunchableHeader = useMemo(() => {
    return (
      <div className="flex flex-col">
        <h1 className="text-white text-2xl font-semibold mr-2">
          {launchableData?.name || ""}
        </h1>
        <div className="flex items-center text-secondary">
          {instanceTypeObject && (
            <Tooltip placement="top" title="Cost to run launchable">
              <div className="flex flex-row items-center">
                <PlayCircleIcon className="w-4 h-4" />
                <div className="flex flex-col text-sm ml-1">
                  $
                  {roundPriceToTwoDigits(
                    instanceTypeObject?.base_price?.amount || "0.00"
                  )}
                  /hr
                </div>
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    );
  }, [launchableData?.name, instanceTypeObject]);

  const renderDeployButton = useMemo(() => {
    return (
      <div>
        <div className="ml-5">
          {!loading && isDeploymentFinished && (
            <div className="flex flex-row gap-2 items-center">
              {/* <h2 className="text-xs text-secondary max-w-[500px]">
                By clicking ‘Deploy Launchable’, I acknowledge and agree that my
                data will be shared with the NVIDIA Cloud Partner providing the
                compute infrastructure for this Launchable.
              </h2> */}
              <div
                className={classNames(
                  isInstancesLoading
                    ? "border border-highlight rounded-[25px] border-highlight"
                    : ""
                )}
              >
                <Button
                  className="justify-center"
                  disabled={
                    !!nameErrorMessage ||
                    !instanceTypeObject ||
                    !isAllowedBaseImage ||
                    deploymentStarted ||
                    allInstancesAvailable.length === 0 ||
                    instanceTypeObject.is_available === false
                  }
                  onClick={onDeploy}
                >
                  {isInstancesLoading ? "Loading..." : "Deploy Launchable"}
                </Button>
              </div>
            </div>
          )}
        </div>
        {!loading && isDeploymentFinished ? (
          <>
            <div className="flex flex-row justify-center">
              <div className="ml-5"></div>
            </div>
          </>
        ) : (
          <div className="">
            <Deploying workspaceId={workspaceId} activeOrgId={activeOrgId} />
          </div>
        )}
      </div>
    );
  }, [
    loading,
    isDeploymentFinished,
    isInstancesLoading,
    nameErrorMessage,
    instanceTypeObject,
    isAllowedBaseImage,
    deploymentStarted,
    allInstancesAvailable.length,
    onDeploy,
    workspaceId,
    activeOrgId,
  ]);

  const renderLoadedConfiguration = useMemo(() => {
    return (
      <LaunchableConfigurationViewFromParams
        intendedInstanceType={intendedInstanceType}
        intendedDiskStorage={intendedDiskStorage || ""}
        instanceLoading={instanceLoading}
        onSetNewCurrentInstanceType={onSetNewCurrentInstanceType}
        containerSelected={launchableData?.containerSelected || null}
        cuda={launchableData?.cuda || ""}
        diskStorage={instanceStorage || ""}
        file={launchableData?.file || ""}
        instanceTypeObject={instanceTypeObject}
        ports={launchableData?.ports || null}
        firewallRules={launchableData?.firewallRules || null}
        python={launchableData?.python || ""}
        workspaceGroupId={launchableData?.workspaceGroupId || ""}
        forceJupyterInstall={
          launchableData?.containerSelected.vmBuild?.forceJupyterInstall ||
          launchableData?.containerSelected?.dockerCompose?.jupyterInstall ||
          false
        }
        lifecycleScript={lifecycleScript}
      />
    );
  }, [
    intendedInstanceType,
    intendedDiskStorage,
    instanceLoading,
    onSetNewCurrentInstanceType,
    launchableData,
    instanceStorage,
    instanceTypeObject,
    lifecycleScript,
  ]);

  const renderMainContent = useCallback(() => {
    // Handle launchable not found state
    if (!loading && launchableNotFound) {
      return <LaunchableNotFound />;
    }

    if (!loading && launchableAccessError) {
      return <LaunchableAccessError isPublic={false} />;
    }

    // Handle normal content state (loading or loaded)
    return (
      <>
        <div className="flex flex-row justify-between mb-5">
          {renderLaunchableHeader}
          {renderDeployButton}
        </div>
        <FlatCard className="min-h-[150px] flex items-center" noBorder={true}>
          {loading && (
            <div className="w-full mb-3 flex text-white">
              <h1 className="text-lg font-semibold mr-2">
                Loading Launchable...
              </h1>
            </div>
          )}
          {!loading ? (
            renderLoadedConfiguration
          ) : (
            <div className="opacity-50 shimmer">
              <LaunchableConfigurationView
                intendedInstanceType={intendedInstanceType}
                intendedDiskStorage={instanceStorage || ""}
                shortFileName="Your Notebook Here"
                loadingInstance={true}
                containerSelected={{
                  verbBuild: {
                    containerUrl: "loading-base-image",
                  },
                }}
                forceJupyterInstall={false}
                cuda={""}
                diskStorage={"256Gi"}
                file={"file name"}
                instanceTypeObject={null}
                ports={null}
                firewallRules={null}
                python={""}
                lifecycleScript={lifecycleScript}
              />
            </div>
          )}
          {!loading && (
            <div className="flex flex-col justify-center">
              <div className="flex flex-col justify-center min-h-[12px]">
                {instanceTypeObject &&
                  instanceTypeObject.is_available === false && (
                    <div className="flex flex-row justify-center mt-5">
                      <span className="text-red-500 dark:text-red-400 text-sm">
                        This compute type is not available at the moment
                      </span>
                    </div>
                  )}
              </div>
            </div>
          )}
        </FlatCard>
        {!loading && <FileViewer file={launchableData?.file || ""} />}
      </>
    );
  }, [
    loading,
    launchableNotFound,
    renderLaunchableHeader,
    renderDeployButton,
    renderLoadedConfiguration,
    intendedInstanceType,
    instanceStorage,
    instanceTypeObject,
    lifecycleScript,
    launchableData?.file,
  ]);

  return (
    <>
      <div className="max-w-7xl mx-auto sm:px-6 lg:px-8 py-10 min-h-screen">
        {renderMainContent()}
      </div>
      <Footer />
    </>
  );
};
